import { useState } from 'react'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { Company, Order, StoryConversion } from '../types'
import { RoiData } from '../hooks/useRoi'
import { CompanyReportPdf } from './CompanyReportPdf'
import { formatRoiValue } from '../utils/roi-copy'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function formatRate(rate: number | null): string {
  if (rate === null || !isFinite(rate)) return '—'
  return `${(rate * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}

/**
 * Tooltip "Conversão por story": aparece no hover do card Receita Influenciada
 * quando a última execução trouxe byStory. Crédito integral (mesmo modelo do
 * byLive) — a soma entre stories pode exceder o total, por isso o rodapé avisa.
 * Identidade visual do story = seus produtos (não há título de story no result).
 */
function StoryConversionTooltip({ stories }: { stories: StoryConversion[] }) {
  const top = stories.slice(0, 6)
  const remaining = stories.length - top.length
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 340,
        maxWidth: '90vw',
        background: '#111827',
        color: '#f9fafb',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        padding: '12px 14px',
        zIndex: 30,
        cursor: 'default',
        textAlign: 'left'
      }}
      onClick={e => e.stopPropagation()}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          color: '#a78bfa',
          marginBottom: 8
        }}
      >
        Conversão por story
      </div>
      {top.map(s => {
        const preview = s.gif || s.thumbnail
        return (
          <div
            key={s.storyKey}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              padding: '6px 0',
              borderBottom: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            {/* Preview da mídia (gif animado > thumbnail), clicável pro vídeo */}
            {preview && (
              <a
                href={s.mediaUrl || undefined}
                target="_blank"
                rel="noreferrer"
                style={{ flexShrink: 0, cursor: s.mediaUrl ? 'pointer' : 'default' }}
                title={s.mediaUrl ? 'Abrir vídeo' : undefined}
                onClick={e => { if (!s.mediaUrl) e.preventDefault() }}
              >
                <img
                  src={preview}
                  alt=""
                  style={{
                    width: 30,
                    height: 42,
                    objectFit: 'cover',
                    borderRadius: 4,
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'block'
                  }}
                />
              </a>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  alignItems: 'baseline'
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0
                  }}
                  title={s.storyTitle || s.products.join(', ') || s.storyKey}
                >
                  {s.storyTitle || s.products[0] || `story ${s.storyKey.slice(0, 8)}…`}
                  {s.products.length > 1 && (
                    <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                      {' '}+{s.products.length - 1}
                    </span>
                  )}
                </span>
                {/* Sem receita atribuída/direta, mostra a influenciada
                    (roxo + etiqueta) pra linha não parecer "R$ 0". */}
                {s.influencedRevenue + s.directRevenue > 0 || !s.viewInfluencedRevenue ? (
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#34d399', flexShrink: 0 }}>
                    {formatCurrency(s.influencedRevenue + s.directRevenue)}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa', flexShrink: 0 }} title="Receita influenciada (viu este vídeo e comprou em até 30d) — não entra no ROI">
                    {formatCurrency(s.viewInfluencedRevenue)} <span style={{ fontSize: 9, fontWeight: 600 }}>infl.</span>
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                <span>
                  taxa <strong style={{ color: s.conversionRate !== null ? '#facc15' : '#6b7280' }}>{formatRate(s.conversionRate)}</strong>
                </span>
                <span>
                  {s.matched + s.directMatched} pedido{s.matched + s.directMatched !== 1 ? 's' : ''}
                </span>
                {s.viewers !== null && (
                  <span>{s.viewers.toLocaleString('pt-BR')} viewers</span>
                )}
              </div>
            </div>
          </div>
        )
      })}
      {remaining > 0 && (
        <div style={{ fontSize: 11, color: '#9ca3af', padding: '6px 0 0' }}>
          +{remaining} outro{remaining !== 1 ? 's' : ''} stor{remaining !== 1 ? 'ies' : 'y'} com conversão
        </div>
      )}
      <div style={{ fontSize: 10, color: '#6b7280', marginTop: 8, lineHeight: 1.4 }}>
        Crédito integral: um produto exibido em 2+ stories credita em todos —
        a soma pode exceder o total. Taxa = compradores ÷ viewers do story.
      </div>
      {/* seta */}
      <div
        style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderTop: '7px solid #111827'
        }}
      />
    </div>
  )
}

interface OrderStats {
  totalRevenue: number
  widdeCount: number
  widdeRevenue: number
  influencedRevenue: number
  directCount: number
  /** Influência por visualização (viu vídeo → comprou depois), sem match de produto. */
  viewInfluencedCount?: number
  viewInfluencedRevenue?: number
}

export interface CompanyStatsCardProps {
  stats: OrderStats
  allOrders: Order[]
  selectedCompany: Company | null
  startDate: string
  endDate: string
  roiData: RoiData | null
  roiLoading: boolean
  datesChanged: boolean
  showToast: (msg: string) => void
  /** byStory da última execução stories — habilita o tooltip por story. */
  storyConversions?: StoryConversion[] | null
}

export function CompanyStatsCard({
  stats,
  allOrders,
  selectedCompany,
  startDate,
  endDate,
  roiData,
  roiLoading,
  datesChanged,
  showToast,
  storyConversions
}: CompanyStatsCardProps) {
  const [showStoryTooltip, setShowStoryTooltip] = useState(false)
  const hasStoryConversions = !!storyConversions && storyConversions.length > 0
  return (
    <div className="card" style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12
        }}
      >
        <h2 style={{ margin: 0 }}>Resumo</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <PDFDownloadLink
            document={
              <CompanyReportPdf
                companyTitle={selectedCompany?.title || ''}
                stats={stats}
                allOrders={allOrders}
                roiData={roiData}
                startDate={startDate}
                endDate={endDate}
              />
            }
            fileName={`relatorio-${(selectedCompany?.title || 'empresa').toLowerCase().replace(/\s+/g, '-')}-${startDate}.pdf`}
            style={{
              background: '#7c3aed',
              border: 'none',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 13,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              textDecoration: 'none',
              transition: 'all 0.15s'
            }}
          >
            {({ loading }) => (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {loading ? 'Gerando...' : 'Baixar PDF'}
              </>
            )}
          </PDFDownloadLink>
          <button
            onClick={() => {
              const s = new Date(startDate)
              const e = new Date(endDate)
              const days =
                Math.round(
                  (e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)
                ) + 1
              const fmt = (d: Date) =>
                `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
              const roiText =
                roiData?.roi !== null && roiData?.roi !== undefined
                  ? `${formatRoiValue(roiData.roi)}x (custo: ${formatCurrency(roiData.proportionalCost)})`
                  : '—'
              const text = [
                `${selectedCompany?.title || ''} | ${fmt(s)} a ${fmt(e)} (${days} dias)`,
                `Total de Pedidos: ${allOrders.length}`,
                `Receita Total: ${formatCurrency(stats.totalRevenue)}`,
                `Pedidos Atribuídos: ${stats.widdeCount}`,
                `Receita Atribuída: ${stats.influencedRevenue > 0 ? formatCurrency(stats.influencedRevenue) : '—'}`,
                `Pedidos Influenciados: ${stats.viewInfluencedCount ?? '—'}`,
                `Receita Influenciada: ${stats.viewInfluencedRevenue ? formatCurrency(stats.viewInfluencedRevenue) : '—'}`,
                `ROI Widde: ${roiText}`
              ].join('\n')
              navigator.clipboard
                .writeText(text)
                .then(() => showToast('Copiado!'))
            }}
            style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
              fontSize: 13,
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.15s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#f3f4f6'
              e.currentTarget.style.borderColor = '#d1d5db'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#f9fafb'
              e.currentTarget.style.borderColor = '#e5e7eb'
            }}
            title="Copiar resumo"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            Copiar resumo
          </button>
        </div>
      </div>
      <div className="dashboard-stats">
        <div
          className="stat-card"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            navigator.clipboard.writeText(String(allOrders.length))
            showToast('Copiado!')
          }}
        >
          <div className="stat-label">Total de Pedidos</div>
          <div className="stat-value">{allOrders.length}</div>
        </div>
        <div
          className="stat-card"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            navigator.clipboard.writeText(formatCurrency(stats.totalRevenue))
            showToast('Copiado!')
          }}
        >
          <div className="stat-label">Receita Total</div>
          <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
        </div>
        <div
          className="stat-card"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            navigator.clipboard.writeText(String(stats.widdeCount))
            showToast('Copiado!')
          }}
        >
          <div className="stat-label">Pedidos Atribuídos</div>
          <div className="stat-value purple">{stats.widdeCount}</div>
        </div>
        <div
          className="stat-card"
          style={{ cursor: 'pointer', position: 'relative' }}
          onClick={() => {
            navigator.clipboard.writeText(
              stats.influencedRevenue > 0
                ? formatCurrency(stats.influencedRevenue)
                : '—'
            )
            showToast('Copiado!')
          }}
          onMouseEnter={() => hasStoryConversions && setShowStoryTooltip(true)}
          onMouseLeave={() => setShowStoryTooltip(false)}
        >
          {showStoryTooltip && hasStoryConversions && (
            <StoryConversionTooltip stories={storyConversions!} />
          )}
          <div className="stat-label">
            Receita Atribuída
            {hasStoryConversions && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#7c3aed',
                  background: '#f3e8ff',
                  border: '1px solid #ddd6fe',
                  borderRadius: 10,
                  padding: '1px 6px',
                  verticalAlign: 'middle',
                  whiteSpace: 'nowrap'
                }}
              >
                por story
              </span>
            )}
          </div>
          <div className="stat-value green">
            {stats.influencedRevenue > 0
              ? formatCurrency(stats.influencedRevenue)
              : '—'}
          </div>
        </div>
        {/* Influência por VISUALIZAÇÃO (novo): viu um vídeo e comprou depois
            (janela 30d), independente de match de produto. Separada da
            atribuição (acima), que exige match de nome e alimenta o ROI.
            Só aparece após uma execução com o backend novo. */}
        {stats.viewInfluencedCount !== undefined && (
          <>
            <div
              className="stat-card"
              style={{ cursor: 'pointer' }}
              title="Pedidos de quem viu um vídeo e comprou depois (30d), independente do produto"
              onClick={() => {
                navigator.clipboard.writeText(String(stats.viewInfluencedCount ?? 0))
                showToast('Copiado!')
              }}
            >
              <div className="stat-label">Pedidos Influenciados</div>
              <div className="stat-value purple">{stats.viewInfluencedCount}</div>
            </div>
            <div
              className="stat-card"
              style={{ cursor: 'pointer' }}
              title="Receita total dos pedidos de quem viu um vídeo e comprou depois (30d)"
              onClick={() => {
                navigator.clipboard.writeText(
                  stats.viewInfluencedRevenue
                    ? formatCurrency(stats.viewInfluencedRevenue)
                    : '—'
                )
                showToast('Copiado!')
              }}
            >
              <div className="stat-label">Receita Influenciada</div>
              <div className="stat-value green">
                {stats.viewInfluencedRevenue
                  ? formatCurrency(stats.viewInfluencedRevenue)
                  : '—'}
              </div>
            </div>
          </>
        )}
        <div
          className="stat-card"
          style={{
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
          title={
            roiData
              ? `Custo proporcional: ${formatCurrency(roiData.proportionalCost)} (${roiData.invoicesFound} invoice${roiData.invoicesFound !== 1 ? 's' : ''} no periodo)`
              : ''
          }
          onClick={() => {
            const roiText =
              roiData?.roi !== null && roiData?.roi !== undefined
                ? `${formatRoiValue(roiData!.roi!)}x`
                : '—'
            navigator.clipboard.writeText(roiText)
            showToast('Copiado!')
          }}
        >
          {datesChanged && (
            <div className="stale-overlay">
              <div className="stale-icon">!</div>
              <div className="stale-text">Processe novamente</div>
            </div>
          )}
          <div className="stat-label">ROI Widde</div>
          <div
            className="stat-value"
            style={{
              color: datesChanged
                ? '#d1d5db'
                : roiLoading
                  ? '#9ca3af'
                  : roiData?.roi !== null && roiData?.roi !== undefined
                    ? roiData.roi >= 0
                      ? '#059669'
                      : '#dc2626'
                    : '#9ca3af'
            }}
          >
            {roiLoading
              ? '...'
              : roiData?.roi !== null && roiData?.roi !== undefined
                ? `${formatRoiValue(roiData.roi)}x`
                : '—'}
          </div>
          {roiData && roiData.proportionalCost > 0 && (
            <div
              style={{
                fontSize: 12,
                color: datesChanged ? '#d1d5db' : '#6b7280',
                marginTop: 4
              }}
            >
              Custo: {formatCurrency(roiData.proportionalCost)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
